import {
  READER_ROUTE_STEP_COUNT,
  readerMaterialKey,
  readerRouteSpecs,
  readerRouteStep,
  type ReaderMaterialReference,
  type ReaderRouteId,
} from "./readerRoutes";

export const READER_STATE_STORAGE_KEY = "atlas:reader-state:v1";
export const READER_STATE_CHANGE_EVENT = "atlas:reader-state-change";

export type ReaderRouteState = {
  currentStep: number;
  openedSteps: number[];
};

export type ReaderResumeState = {
  routeId: ReaderRouteId;
  stepIndex: number;
  material: ReaderMaterialReference;
};

export type ReaderState = {
  version: 1;
  resume: ReaderResumeState | null;
  routes: Partial<Record<ReaderRouteId, ReaderRouteState>>;
  saved: ReaderMaterialReference[];
};

const EMPTY_READER_STATE: ReaderState = {
  version: 1,
  resume: null,
  routes: {},
  saved: [],
};

const stableIdPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const routeIds = new Set<ReaderRouteId>(readerRouteSpecs.map((route) => route.id));

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isReaderRouteId(value: unknown): value is ReaderRouteId {
  return typeof value === "string" && routeIds.has(value as ReaderRouteId);
}

export function isReaderMaterialReference(value: unknown): value is ReaderMaterialReference {
  if (!isRecord(value) || typeof value.kind !== "string") return false;
  if (value.kind === "essay") {
    return typeof value.slug === "string" && stableIdPattern.test(value.slug);
  }
  if (value.kind === "crystal") {
    return typeof value.chapterSlug === "string"
      && stableIdPattern.test(value.chapterSlug)
      && typeof value.crystalId === "string"
      && stableIdPattern.test(value.crystalId);
  }
  return false;
}

function normalizeRouteState(value: unknown): ReaderRouteState | null {
  if (!isRecord(value) || !Number.isInteger(value.currentStep)) return null;
  const currentStep = Number(value.currentStep);
  if (currentStep < 1 || currentStep > READER_ROUTE_STEP_COUNT || !Array.isArray(value.openedSteps)) {
    return null;
  }
  const openedSteps = [...new Set(value.openedSteps.filter((step) => (
    Number.isInteger(step) && Number(step) >= 1 && Number(step) <= READER_ROUTE_STEP_COUNT
  )).map(Number))].sort((left, right) => left - right);
  if (!openedSteps.includes(currentStep)) openedSteps.push(currentStep);
  openedSteps.sort((left, right) => left - right);
  return { currentStep, openedSteps };
}

function normalizeResume(value: unknown): ReaderResumeState | null {
  if (
    !isRecord(value)
    || !isReaderRouteId(value.routeId)
    || !Number.isInteger(value.stepIndex)
    || !isReaderMaterialReference(value.material)
  ) return null;
  const stepIndex = Number(value.stepIndex);
  const expected = readerRouteStep(value.routeId, stepIndex);
  if (!expected || readerMaterialKey(expected) !== readerMaterialKey(value.material)) return null;
  return { routeId: value.routeId, stepIndex, material: value.material };
}

export function normalizeReaderState(value: unknown): ReaderState {
  if (!isRecord(value) || value.version !== 1) return EMPTY_READER_STATE;

  const routes: ReaderState["routes"] = {};
  if (isRecord(value.routes)) {
    for (const route of readerRouteSpecs) {
      const normalized = normalizeRouteState(value.routes[route.id]);
      if (normalized) routes[route.id] = normalized;
    }
  }

  const saved: ReaderMaterialReference[] = [];
  const savedKeys = new Set<string>();
  if (Array.isArray(value.saved)) {
    for (const candidate of value.saved) {
      if (!isReaderMaterialReference(candidate)) continue;
      const key = readerMaterialKey(candidate);
      if (savedKeys.has(key)) continue;
      savedKeys.add(key);
      saved.push(candidate);
      if (saved.length === 50) break;
    }
  }

  const resume = normalizeResume(value.resume);
  if (resume && !routes[resume.routeId]) {
    routes[resume.routeId] = {
      currentStep: resume.stepIndex,
      openedSteps: [resume.stepIndex],
    };
  }

  return { version: 1, resume, routes, saved };
}

function parseReaderState(raw: string | null) {
  if (!raw) return EMPTY_READER_STATE;
  try {
    return normalizeReaderState(JSON.parse(raw));
  } catch {
    return EMPTY_READER_STATE;
  }
}

let fallbackState = EMPTY_READER_STATE;
let cachedRaw: string | null | undefined;
let cachedState = EMPTY_READER_STATE;

function readStoredState() {
  if (typeof window === "undefined") return { available: false, raw: null };
  try {
    return { available: true, raw: window.localStorage.getItem(READER_STATE_STORAGE_KEY) };
  } catch {
    return { available: false, raw: null };
  }
}

export function getReaderStateSnapshot(): ReaderState {
  const stored = readStoredState();
  if (!stored.available) return fallbackState;
  if (cachedRaw === stored.raw) return cachedState;
  cachedRaw = stored.raw;
  cachedState = parseReaderState(stored.raw);
  fallbackState = cachedState;
  return cachedState;
}

export function getReaderStateServerSnapshot(): ReaderState {
  return EMPTY_READER_STATE;
}

function emitReaderStateChange() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(READER_STATE_CHANGE_EVENT));
}

function writeReaderState(nextState: ReaderState) {
  const normalized = normalizeReaderState(nextState);
  const raw = JSON.stringify(normalized);
  let stored = false;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(READER_STATE_STORAGE_KEY, raw);
      stored = true;
    } catch {
      // Continue with the in-memory fallback when storage is blocked or full.
    }
  }
  fallbackState = normalized;
  cachedState = normalized;
  cachedRaw = stored ? raw : undefined;
  emitReaderStateChange();
  return normalized;
}

export function subscribeReaderState(onChange: () => void) {
  if (typeof window === "undefined") return () => undefined;
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== READER_STATE_STORAGE_KEY) return;
    cachedRaw = undefined;
    onChange();
  };
  window.addEventListener(READER_STATE_CHANGE_EVENT, onChange);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(READER_STATE_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", handleStorage);
  };
}

export function recordReaderStep(
  routeId: ReaderRouteId,
  stepIndex: number,
  material: ReaderMaterialReference,
) {
  const expected = readerRouteStep(routeId, stepIndex);
  if (!expected || readerMaterialKey(expected) !== readerMaterialKey(material)) {
    return getReaderStateSnapshot();
  }
  const state = getReaderStateSnapshot();
  const previousRoute = state.routes[routeId];
  const openedSteps = [...new Set([...(previousRoute?.openedSteps ?? []), stepIndex])]
    .sort((left, right) => left - right);
  return writeReaderState({
    ...state,
    resume: { routeId, stepIndex, material },
    routes: {
      ...state.routes,
      [routeId]: { currentStep: stepIndex, openedSteps },
    },
  });
}

export function toggleSavedReaderMaterial(material: ReaderMaterialReference) {
  const state = getReaderStateSnapshot();
  const key = readerMaterialKey(material);
  const alreadySaved = state.saved.some((candidate) => readerMaterialKey(candidate) === key);
  const saved = alreadySaved
    ? state.saved.filter((candidate) => readerMaterialKey(candidate) !== key)
    : [material, ...state.saved].slice(0, 50);
  return writeReaderState({ ...state, saved });
}

export function clearReaderState() {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(READER_STATE_STORAGE_KEY);
    } catch {
      // The in-memory state is still cleared below.
    }
  }
  fallbackState = EMPTY_READER_STATE;
  cachedState = EMPTY_READER_STATE;
  cachedRaw = null;
  emitReaderStateChange();
  return EMPTY_READER_STATE;
}

